import React, { useEffect, useMemo } from 'react';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import type { JSErrorBoundaryProps } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { getErrorType } from '@atlassian/ui-modifications-analytics';
import { useAdjustmentsContextActions } from '@atlassian/ui-modifications-core/controllers';
import type { RootUiModificationsQueryType } from '@atlassian/ui-modifications-core/gql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { UiModificationsErrorHandler } from '@atlassian/ui-modifications-core';
import {
	isTriggerPointSupported,
	isExecutionContextSupported,
} from '@atlassian/ui-modifications-core/src/controllers/view-configuration/index.tsx';
import type { ExecutionContext } from '@atlassian/ui-modifications-core/types';
import { ForgeAppRenderer } from './forge-app-renderer/index.tsx';
import { uiModificationsRootEntrypoint } from './entrypoint.tsx';
import type { RootEntrypointWrapperRuntimeProps } from './root-entrypoint-wrapper/index.tsx';

export type UiModificationsJiraRootProps = {
	containerId: string;
	containerPackageName: string;
	queryParams: RootUiModificationsQueryType['variables'];
	/**
	 * If not provided, the default `UiModificationsErrorHandler` error fallback will be used.
	 */
	errorFallback?: JSErrorBoundaryProps['fallback'];
	executionContext: ExecutionContext;
} & Omit<
	RootEntrypointWrapperRuntimeProps,
	'cloudId' | 'activationId' | 'ForgeAppRenderer' | 'executionContext'
>;

const defaultErrorFallback = () => <UiModificationsErrorHandler />;

/**
 * Jira UI modifications root component with entrypoint support and query loading.
 */
export const UiModificationsJiraRoot = ({
	containerId,
	containerPackageName,
	errorFallback,
	queryParams,
	executionContextKey,
	executionContext,
	triggerPointKey,
	numberOfSupportedFields,
	resetFormFieldsCounter,
}: UiModificationsJiraRootProps) => {
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		uiModificationsRootEntrypoint,
		queryParams,
	);
	const { setShouldUiModificationsLoad } = useAdjustmentsContextActions();

	const shouldUiModificationsLoad =
		executionContext &&
		isExecutionContextSupported(executionContext) &&
		isTriggerPointSupported(queryParams.uiModificationsContext.viewType, triggerPointKey);

	// In case that the UI modifications are not supposed to load,
	// we need to set the shouldUiModificationsLoad to 'no' to have proper context state
	// otherwise the context state will be set to 'yes' by the root component
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		shouldUiModificationsLoad ? entryPointActions.load() : setShouldUiModificationsLoad('no');
	}, [
		entryPointActions,
		queryParams,
		executionContextKey,
		shouldUiModificationsLoad,
		setShouldUiModificationsLoad,
	]);

	const runtimeProps = useMemo<RootEntrypointWrapperRuntimeProps>(
		() => ({
			cloudId,
			activationId,
			executionContextKey,
			executionContext,
			triggerPointKey,
			numberOfSupportedFields,
			resetFormFieldsCounter,
			ForgeAppRenderer,
		}),
		[
			activationId,
			cloudId,
			executionContextKey,
			executionContext,
			triggerPointKey,
			numberOfSupportedFields,
			resetFormFieldsCounter,
		],
	);

	return (
		<JiraEntryPointContainer
			entryPointReferenceSubject={entryPointReferenceSubject}
			id={containerId}
			packageName={containerPackageName}
			fallback={<></>}
			errorFallback={errorFallback ?? defaultErrorFallback}
			errorAttributes={(error: Error) => ({
				errorType: getErrorType(error),
				viewType: queryParams.uiModificationsContext.viewType,
			})}
			runtimeProps={runtimeProps}
		/>
	);
};
